import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { startProUpgrade } from '@/utils/upgrade';
import CommentSection from '../CommentSection';
import VersionHistory from '../VersionHistory';
import AiSuggestSubtasksModal from './AiSuggestSubtasksModal';

import { API, STATUS_CONFIG } from './constants';
import {
  isRiskAssessmentTask,
  isLawsRegulationsTask,
  isEmergencyPlanTask,
  isStakeholderTask,
  isInternalAuditTask,
  isGoalsTask,
} from './taskHelpers';
import {
  LawsRegulationsGuidance,
  EmergencyPlanGuidance,
  StakeholderGuidance,
  InternalAuditGuidance,
  GoalsGuidance,
} from './guidance';
import {
  WhySection,
  RecommendedAction,
  AlternativeUpload,
  ReadMoreSection,
  MetaInfoSection,
  DocumentsSection,
  OptionalAISupport,
} from './sections';

// ============================================================================
// RISK ASSESSMENT CHOICE - Special component for risk assessment tasks
// ============================================================================
function RiskAssessmentChoice({ task, fileInputRef, uploading, files, onFileUpload }) {
  const { t } = useTranslation();
  const [showUpload, setShowUpload] = useState(false);
  const hasFiles = files && files.length > 0;

  return (
    <div data-testid="risk-assessment-choice">
      {/* Why this is important */}
      <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-900 mb-1">Kjerneoppgave i HMS-systemet</p>
            <p className="text-sm text-amber-800">
              Risikovurderingen er grunnlaget for hele HMS-systemet. Alle prosedyrer,
              sjekklister og opplæringsplaner skal håndtere risikoer du identifiserer her.
            </p>
          </div>
        </div>
      </div>

      {/* Choice section */}
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
        Hvordan vil du gjennomføre risikovurderingen?
      </p>

      <div className="space-y-3">
        {/* Option 1: Use Systemklar tool (Recommended) */}
        <Link
          to="/risk"
          className="block bg-blue-50 border-2 border-blue-200 rounded-xl p-4 hover:border-blue-400 hover:bg-blue-100 transition-all group"
          data-testid="use-risk-tool-btn"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-blue-900">Bruk Systemklars verktøy</span>
                <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-xs font-medium rounded">Anbefalt</span>
              </div>
              <p className="text-sm text-blue-700 mb-2">
                Guidet prosess med risikomatrise og automatisk kobling til tiltak
              </p>
              <div className="space-y-1">
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Tiltak blir automatisk oppgaver
                </p>
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Visuell risikomatrise
                </p>
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  AI-forslag til tiltak
                </p>
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Ferdig rapport til nedlasting
                </p>
              </div>
            </div>
            <svg className="w-5 h-5 text-blue-400 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>

        {/* Option 2: Upload existing document */}
        <div className="border border-slate-700 rounded-xl p-4 bg-slate-800/50">
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="w-full flex items-start gap-3 text-left"
            data-testid="upload-existing-btn"
          >
            <div className="flex-shrink-0 w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <div className="flex-1">
              <span className="font-medium text-white">{t('tasks.uploadExisting', 'Last opp eksisterende dokument')}</span>
              <p className="text-sm text-slate-400 mt-0.5">
                {t('tasks.alreadyHaveDocument', 'Har du allerede et dokument fra før?')}
              </p>
            </div>
            <svg className={`w-5 h-5 text-slate-400 transition-transform ${showUpload ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showUpload && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              {/* Warnings */}
              <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-xs font-medium text-amber-300 mb-2">{t('tasks.uploadLimitations', 'Begrensninger ved opplasting:')}</p>
                <div className="space-y-1">
                  <p className="text-xs text-amber-200/80 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {t('tasks.noAutoTasks', 'Tiltak blir ikke automatisk oppgaver')}
                  </p>
                  <p className="text-xs text-amber-200/80 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {t('tasks.noProcedureLink', 'Ingen kobling til prosedyrer i systemet')}
                  </p>
                  <p className="text-xs text-amber-200/80 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {t('tasks.manualFollowup', 'Du må opprette oppfølgingsoppgaver manuelt')}
                  </p>
                </div>
              </div>

              {/* Upload button */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={onFileUpload}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    Laster opp...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Velg fil å laste opp
                  </>
                )}
              </button>

              {/* Show uploaded files */}
              {hasFiles && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Opplastede dokumenter:</p>
                  <div className="space-y-1">
                    {files.map((file, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {file.filename || file.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TASK DETAIL MODAL COMPONENT
// ============================================================================
export function TaskDetailModal({ task, subtasks = [], onClose, onStatusChange, onDelete, formatDate, onTaskUpdated }) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAiSuggest, setShowAiSuggest] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const fileInputRef = React.useRef(null);

  // Fetch files on mount
  useEffect(() => {
    fetchFiles();
  }, [task.id]);

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/tasks/${task.id}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('tasks.documents.fileTooLarge') || 'Filen er for stor. Maks 10MB.');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/ops/tasks/${task.id}/files`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t('tasks.documents.uploaded') || 'Dokument lastet opp');
      fetchFiles();
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(t('tasks.documents.couldNotUpload'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (fileRecord) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/files/${fileRecord.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileRecord.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('tasks.documents.couldNotDownload'));
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(`${API}/ops/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('tasks.documents.deleted'));
      fetchFiles();
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      toast.error(t('tasks.documents.couldNotDelete'));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const statusActions = {
    not_started: [
      { status: 'in_progress', label: t('tasks.actions.start'), color: 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold' }
    ],
    in_progress: [
      { status: 'completed', label: t('tasks.actions.markComplete'), color: 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold' },
      { status: 'not_started', label: t('tasks.actions.reopen'), color: 'border border-slate-600 text-slate-300 hover:bg-slate-800' }
    ],
    completed: [
      { status: 'in_progress', label: t('tasks.actions.reopenInProgress'), color: 'border border-slate-600 text-slate-300 hover:bg-slate-800' },
      { status: 'not_started', label: t('tasks.actions.reopenNotStarted'), color: 'border border-slate-600 text-slate-300 hover:bg-slate-800' }
    ]
  };

  const actions = statusActions[task.status] || statusActions.not_started;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="task-detail-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                  task.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                  task.status === 'in_progress' ? 'bg-cyan-500/20 text-cyan-400' :
                  'bg-slate-700/50 text-slate-400'
                }`}>
                  {STATUS_CONFIG[task.status]?.label || 'Ukjent'}
                </span>
                {task.is_overdue && task.status !== 'completed' && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-rose-500/20 text-rose-400">
                    {t('tasks.overdue')}
                  </span>
                )}
                {task.locked && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                    {t('tasks.requiresPro')}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-white">{task.title}</h2>
              {task.status !== 'completed' && (
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-cyan-300" data-testid="step-toward-control">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  {t('tasks.stepTowardControl')}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              data-testid="close-task-modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-950/50">
          {/* Cross-cert coverage banner */}
          {Array.isArray(task.covers_certs) && task.covers_certs.length > 1 && !task.stricter_than_base && (
            <div className="mb-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3" data-testid="coverage-banner">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-emerald-300">{t('tasks.coversMultipleCerts')}</p>
                  <p className="text-xs text-emerald-200/80 mt-0.5">
                    {task.covers_certs.map(c => c.replace('ISO_', 'ISO ')).join(' + ')}
                  </p>
                  {Array.isArray(task.coverage_notes) && task.coverage_notes.length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {task.coverage_notes.slice(-3).map((n, i) => (
                        <li key={i} className="text-xs text-emerald-200/70">• {n.note}</li>
                      ))}
                    </ul>
                  )}
                  {task.covers_certs.some(c => c.startsWith('ISO_')) && (
                    <a
                      href={`/gap-analysis?from_task=${task.id}`}
                      className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-cyan-400 hover:text-cyan-300"
                      data-testid="quick-gap-check-link"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 3l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      {t('tasks.runQuickGapCheck')}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stricter-than-base warning banner */}
          {task.stricter_than_base && (
            <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="stricter-banner">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-300 mb-1">{t('tasks.stricterWarningTitle')}</p>
                  <p className="text-xs text-amber-200/90">{t('tasks.stricterWarningBody')}</p>
                  {task.covered_by_task_id && (
                    <p className="text-xs text-amber-200/60 mt-2 font-mono">{t('tasks.linkedToIkTask')}: {task.covered_by_task_id.substring(0, 8)}…</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AI GAP-analysis verdict panel */}
          {task.gap_result_status && (
            <div
              className={`mb-4 rounded-xl p-4 border ${
                task.gap_result_status === 'met' ? 'bg-emerald-500/10 border-emerald-500/30' :
                task.gap_result_status === 'partial' ? 'bg-amber-500/10 border-amber-500/30' :
                'bg-rose-500/10 border-rose-500/30'
              }`}
              data-testid="ai-verdict-panel"
            >
              <div className="flex items-start gap-3">
                <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  task.gap_result_status === 'met' ? 'text-emerald-400' :
                  task.gap_result_status === 'partial' ? 'text-amber-400' : 'text-rose-400'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 3l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-white">{t('tasks.aiGapVerdict')}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      task.gap_result_status === 'met' ? 'bg-emerald-500/20 text-emerald-300' :
                      task.gap_result_status === 'partial' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-rose-500/20 text-rose-300'
                    }`}>
                      {t(`gap.${task.gap_result_status}`)} • {task.gap_result_confidence ?? 0}%
                    </span>
                    {task.gap_result_clause && (
                      <span className="text-xs text-slate-400">
                        {(() => {
                          const [stdId, cl] = task.gap_result_clause.split(':');
                          const prettyStd = (stdId || '').replace('iso_', 'ISO ').replace('_2026', ':2026');
                          return `${prettyStd} §${cl || ''}`;
                        })()}
                      </span>
                    )}
                  </div>
                  {task.gap_result_summary && (
                    <p className="text-xs text-slate-300 mt-2">{task.gap_result_summary}</p>
                  )}
                  {task.gap_result_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {t('tasks.aiAnalyzedAt')}: {new Date(task.gap_result_at).toLocaleString()}
                    </p>
                  )}
                  <a
                    href={`/gap-analysis?from_task=${task.id}`}
                    className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-cyan-400 hover:text-cyan-300"
                    data-testid="view-gap-detail-link"
                  >
                    {t('tasks.viewFullGapAnalysis')}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                  {(task.gap_result_status === 'partial' || task.gap_result_status === 'missing') && (
                    <button
                      onClick={() => setShowAiSuggest(true)}
                      className="inline-flex items-center gap-1.5 mt-3 ml-3 px-3 py-1.5 bg-cyan-500 text-slate-950 rounded-lg text-xs font-semibold hover:bg-cyan-400 shadow-lg shadow-cyan-500/20"
                      data-testid="ai-suggest-btn"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 3l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      {t('tasks.aiSuggest.openBtn')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Subtasks list (GAP-generated action plan) */}
          {subtasks && subtasks.length > 0 && (
            <div className="mb-4 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4" data-testid="subtasks-section">
              {(() => {
                const doneCount = subtasks.filter(s => s.status === 'completed').length;
                const total = subtasks.length;
                const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
                const allDone = doneCount === total && total > 0;
                return (
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <p className="text-sm font-semibold text-white">
                        {t('tasks.subtasksTitle')}
                      </p>
                      <p
                        className={`text-xs font-mono tabular-nums ${allDone ? 'text-emerald-300' : 'text-cyan-300'}`}
                        data-testid="subtasks-progress-text"
                      >
                        {t('tasks.subtasksProgress', { done: doneCount, total })} · {pct}%
                      </p>
                    </div>
                    <div
                      className="h-2 w-full overflow-hidden rounded-full bg-slate-800/60"
                      role="progressbar"
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      data-testid="subtasks-progress-bar"
                    >
                      <div
                        className={`h-full rounded-full transition-all duration-500 ease-out ${
                          allDone
                            ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.5)]'
                            : 'bg-gradient-to-r from-cyan-400 to-blue-500'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {allDone
                        ? t('tasks.subtasksAllDone')
                        : t('tasks.subtasksHelper')}
                    </p>
                  </div>
                );
              })()}
              <ul className="space-y-2">
                {subtasks.map((sub) => {
                  const isDone = sub.status === 'completed';
                  const nextStatus = isDone ? 'not_started' : 'completed';
                  return (
                    <li
                      key={sub.id}
                      className={`flex items-start gap-3 rounded-lg border p-3 transition-all duration-200 ${
                        isDone
                          ? 'border-emerald-500/20 bg-emerald-500/5'
                          : 'border-white/10 bg-slate-950/40 hover:border-cyan-500/30 hover:bg-slate-950/60'
                      }`}
                      data-testid={`subtask-row-${sub.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => onStatusChange(sub.id, nextStatus)}
                        className={`group mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-all duration-200 ease-out hover:scale-110 active:scale-90 ${
                          isDone
                            ? 'border-emerald-500 bg-emerald-500 text-slate-950 shadow-[0_0_10px_rgba(16,185,129,0.4)]'
                            : 'border-slate-500 bg-transparent hover:border-cyan-400 hover:shadow-[0_0_10px_rgba(34,211,238,0.35)]'
                        }`}
                        title={isDone ? t('tasks.subtasksReopen') : t('tasks.subtasksComplete')}
                        aria-label={isDone ? t('tasks.subtasksReopen') : t('tasks.subtasksComplete')}
                        data-testid={`subtask-toggle-${sub.id}`}
                      >
                        {isDone ? (
                          <svg className="h-3.5 w-3.5 animate-[bounceOnce_0.3s_ease-out]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="h-3 w-3 text-slate-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium transition-colors ${isDone ? 'text-slate-400 line-through' : 'text-white'}`}>
                          {sub.title}
                        </p>
                        {sub.deadline && (
                          <p className="text-xs text-slate-500 mt-1">
                            {t('tasks.deadline')}: {formatDate ? formatDate(sub.deadline) : new Date(sub.deadline).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {sub.priority && (
                        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 ${
                          sub.priority === 'high' ? 'bg-rose-500/10 text-rose-300' :
                          sub.priority === 'medium' ? 'bg-amber-500/10 text-amber-300' :
                          'bg-slate-500/10 text-slate-400'
                        }`}>
                          {sub.priority}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Special handling for Risk Assessment task */}
          {isRiskAssessmentTask(task) ? (
            <RiskAssessmentChoice
              task={task}
              fileInputRef={fileInputRef}
              uploading={uploading}
              files={files}
              onFileUpload={handleFileUpload}
            />
          ) : isLawsRegulationsTask(task) ? (
            <LawsRegulationsGuidance
              task={task}
              fileInputRef={fileInputRef}
              uploading={uploading}
              files={files}
              onFileUpload={handleFileUpload}
            />
          ) : isEmergencyPlanTask(task) ? (
            <EmergencyPlanGuidance
              task={task}
              fileInputRef={fileInputRef}
              uploading={uploading}
              files={files}
              onFileUpload={handleFileUpload}
            />
          ) : isStakeholderTask(task) ? (
            <StakeholderGuidance
              task={task}
              fileInputRef={fileInputRef}
              uploading={uploading}
              files={files}
              onFileUpload={handleFileUpload}
            />
          ) : isInternalAuditTask(task) ? (
            <InternalAuditGuidance
              task={task}
              fileInputRef={fileInputRef}
              uploading={uploading}
              files={files}
              onFileUpload={handleFileUpload}
            />
          ) : isGoalsTask(task) ? (
            <GoalsGuidance
              task={task}
              fileInputRef={fileInputRef}
              uploading={uploading}
              files={files}
              onFileUpload={handleFileUpload}
            />
          ) : (
            <>
              {/* 1. HVORFOR - Why this task matters (if available) */}
              <WhySection task={task} />

              {/* 2. ANBEFALT HANDLING - What to actually DO */}
              <RecommendedAction
                task={task}
                fileInputRef={fileInputRef}
                uploading={uploading}
                files={files}
              />

              {/* 3. ALTERNATIV - Upload existing document */}
              <AlternativeUpload
                task={task}
                fileInputRef={fileInputRef}
                uploading={uploading}
                files={files}
              />

              {/* 4. VALGFRI AI-STØTTE - Secondary AI option */}
              {task.source && task.source.includes('flow') && (
                <OptionalAISupport task={task} onDocumentGenerated={onTaskUpdated} />
              )}
            </>
          )}

          {/* DOKUMENTER - List of uploaded files */}
          <DocumentsSection
            files={files}
            loadingFiles={loadingFiles}
            uploading={uploading}
            fileInputRef={fileInputRef}
            onDownload={handleDownload}
            onDelete={handleDeleteFile}
            formatFileSize={formatFileSize}
          />

          {/* LES MER - Expandable detailed description */}
          {task.description && (
            <ReadMoreSection task={task} />
          )}

          {/* Meta info - Compact */}
          <MetaInfoSection task={task} formatDate={formatDate} />

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif"
            data-testid="file-input"
          />
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-slate-800 bg-slate-900">
          {showDeleteConfirm ? (
            <div className="flex items-center justify-between">
              <p className="text-sm text-rose-400">{t('tasks.confirmDelete') || 'Er du sikker på at du vil slette denne oppgaven?'}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-slate-600 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => onDelete(task.id)}
                  className="px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-400"
                  data-testid="confirm-delete-task"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 text-rose-400 hover:bg-rose-500/10 rounded-lg text-sm font-medium transition-colors"
                data-testid="delete-task-btn"
              >
                {t('tasks.deleteTask') || 'Slett oppgave'}
              </button>
              <div className="flex gap-2">
                {!task.locked && actions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => onStatusChange(task.id, action.status)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${action.color}`}
                    data-testid={`status-action-${action.status}`}
                  >
                    {action.label}
                  </button>
                ))}
                {task.locked && (
                  <button
                    onClick={startProUpgrade}
                    className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 shadow-lg shadow-cyan-500/25"
                    data-testid="upgrade-from-modal"
                  >
                    {t('tasks.upgrade.button')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Comments */}
        <CommentSection entityType="task" entityId={task.id} />

        {/* Version History */}
        <VersionHistory entityType="task" entityId={task.id} onRestored={onTaskUpdated} />
      </div>

      {showAiSuggest && (
        <AiSuggestSubtasksModal
          task={task}
          onClose={() => setShowAiSuggest(false)}
          onCreated={() => { setShowAiSuggest(false); onTaskUpdated && onTaskUpdated(); }}
          t={t}
        />
      )}
    </div>
  );
}
